import { StrictMode, lazy, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, ClerkLoading, useAuth, SignedIn, SignedOut } from "@clerk/clerk-react";
import { clerkLocalization } from "./clerk-locale";
import { Landing } from "./landing";
import { SignInPage, SignUpPage, SsoCallbackPage } from "./routes/sign-in";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router";
import { setTokenProvider } from "./api/client";
import { SessionGate, hasOfflineSession, lastSignedInUserId } from "./session-gate";
import { ErrorBoundary } from "./error-boundary";
import { AppLayout } from "./app-layout";
import { TripLayout } from "./trip-layout";
import TripsPage from "./routes/trips";
const TripItineraryPage = lazy(() => import("./routes/trip-itinerary"));
import { ThemeProvider } from "@/components/theme/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { registerServiceWorker, purgeApiCache } from "./sw-register";
import { clearScreenCache } from "./use-screen";
import { registerUploadHost } from "@/lib/safe-url";
import "@/app/globals.css";

/**
 * **자주 안 여는 화면은 따로 싣는다.** 전부 한 덩어리로 묶었더니 첫 화면이
 * 1.12MB(gzip 337KB)를 다 받고 파싱할 때까지 아무것도 안 보였다. 지도(leaflet)·
 * 차트·엑셀은 해당 화면에서만 필요하다.
 *
 * 여행 목록과 일정(첫 화면)은 **일부러 남겨 둔다** — 대부분이 거기로 들어오는데
 * 나눠 두면 왕복이 하나 더 붙는다.
 *
 * admin은 한 모듈이라 이름 있는 내보내기를 default로 감싼다. 같은 청크를 쓴다.
 */
type AdminExport = keyof typeof import("./routes/admin");
const fromAdmin = (name: AdminExport) =>
  lazy(() => import("./routes/admin").then((m) => ({ default: m[name] })));

const JoinPage = lazy(() => import("./routes/join"));
const SharePage = lazy(() => import("./routes/share"));
const NewTripPage = lazy(() => import("./routes/trip-new"));
const ProfilePage = lazy(() => import("./routes/profile"));
const HelpPage = lazy(() => import("@/app/(app)/help/page"));
const TripChecklistPage = lazy(() => import("./routes/trip-checklist"));
const TripBudgetPage = lazy(() => import("./routes/trip-budget"));
const TripMapPage = lazy(() => import("./routes/trip-map"));
const TripJournalPage = lazy(() => import("./routes/trip-journal"));
const TripAlbumPage = lazy(() => import("./routes/trip-album"));
const TripSettingsPage = lazy(() => import("./routes/trip-settings"));
const TripPrintPage = lazy(() => import("./routes/trip-print"));
const TripReportPage = lazy(() => import("./routes/trip-report"));
const AdminLayout = fromAdmin("AdminLayout");
const AdminDashboardPage = fromAdmin("AdminDashboardPage");
const AdminUsersPage = fromAdmin("AdminUsersPage");
const AdminUserDetailPage = fromAdmin("AdminUserDetailPage");
const AdminTripsPage = fromAdmin("AdminTripsPage");
const AdminTripDetailPage = fromAdmin("AdminTripDetailPage");
const AdminToolsPage = fromAdmin("AdminToolsPage");
const AdminLogsPage = fromAdmin("AdminLogsPage");

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error("VITE_CLERK_PUBLISHABLE_KEY가 없습니다 (web/.env)");
}

/**
 * Clerk의 토큰 공급자를 API 클라이언트에 심는다.
 *
 * **모듈 로드 시점이 아니라 여기서** 하는 이유: `getToken()`은 만료 전에 알아서
 * 갱신해 주는 함수라, 한 번 받은 문자열을 붙잡아 두면 만료된 토큰을 계속 쓰게 된다.
 */
function TokenBridge({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  setTokenProvider(() => getToken());
  return <>{children}</>;
}

/**
 * 로그인 관문. 로그인 전이면 로그인 화면, 후면 계정 연결(SessionGate)을 거쳐 본문.
 *
 * **초대 화면은 이 바깥에 둔다** — 무슨 여행에 초대됐는지 못 보고 로그인부터 해야
 * 하면 링크를 안 누른다. Next의 join 페이지도 같은 이유로 공개였다.
 */
/**
 * Clerk이 세션을 확인하는 동안 보여줄 껍데기.
 *
 * 왜: `SignedIn`·`SignedOut` 둘 다 확인이 끝나기 전에는 **아무것도 안 그린다.**
 * 그래서 첫 픽셀이 Clerk의 `/v1/client`(실측 250ms)를 기다렸다 — 그동안 흰
 * 화면이다. 머리글은 로그인 여부와 무관하므로 먼저 그릴 수 있다.
 *
 * 실제 대기가 줄지는 않는다. **뭔가 보이는 것과 흰 화면은 다르다.**
 */
function AuthSkeleton() {
  // **오프라인이면 여기서 영원히 기다린다.** Clerk이 세션을 확인하려면 네트워크가
  // 필요한데(clerk.gotravel.day), 끊겨 있으면 SignedIn·SignedOut 둘 다 안 그려진다.
  // 껍데기만 도는 화면을 보여 주느니 이유를 말한다. 연결되면 알아서 이어진다.
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setStuck(true), 6000);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="flex min-h-dvh flex-1 flex-col">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center gap-3 px-4 sm:gap-6 sm:px-6">
          <span className="shrink-0 whitespace-nowrap bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-lg font-bold tracking-tight text-transparent">
            여행가자
          </span>
          <div className="h-4 w-16 animate-pulse rounded bg-muted" />
          <div className="ml-auto h-8 w-8 animate-pulse rounded-full bg-muted" />
        </div>
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6">
        {stuck ? (
          <p className="text-sm text-muted-foreground">
            로그인 상태를 확인하는 중이에요. 인터넷 연결을 확인해주세요 — 연결되면 자동으로 이어집니다.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="h-6 w-32 animate-pulse rounded bg-muted" />
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
            <div className="h-24 animate-pulse rounded-lg bg-muted" />
          </div>
        )}
      </main>
    </div>
  );
}

/**
 * 끊긴 채로 앱을 다시 열었을 때 쓸 신원.
 *
 * Clerk은 세션을 확인하려면 네트워크가 필요해서, 오프라인이면 `SignedIn`도
 * `SignedOut`도 안 그려진다 — 서비스 워커가 화면과 데이터를 다 갖고 있어도
 * 그 앞에서 막힌다. 비행기 모드에서 정산을 다시 열어 보는 건 실제로 있는
 * 상황이라, **마지막 사용자로 캐시만 보여 준다.**
 *
 * 쓰기는 막지 않아도 된다 — 어차피 API가 안 나간다. 화면 위에는
 * `OfflineBanner`가 "마지막으로 본 내용"이라고 알린다.
 */
function useOfflineEntry(): string | null {
  // **Clerk이 아직 확인 중일 때만** 오프라인 진입을 따진다. 이 줄이 없으면
  // 멀쩡히 로그인된 사람도 5초 뒤 아래 감시가 켜지고, /health가 성공하니까
  // 새로고침이 끝없이 돈다 — 실제로 그랬다(2026-09-26).
  const { isLoaded } = useAuth();
  // **`navigator.onLine`만 믿지 않는다.** 실측: 네트워크를 끊고 새로고침해도
  // 새 문서에서는 true로 나온다(브라우저는 "랜선이 꽂혀 있다"까지만 안다 —
  // 기내 와이파이나 캡티브 포털도 마찬가지다). 그래서 **증상**을 본다:
  // Clerk이 몇 초가 지나도 안 뜨면 그게 끊긴 것이다.
  const [give, setGive] = useState(() => typeof navigator !== "undefined" && !navigator.onLine);
  useEffect(() => {
    if (give) return;
    const t = setTimeout(() => setGive(true), 5000);
    const off = () => setGive(true);
    window.addEventListener("offline", off);
    return () => {
      clearTimeout(t);
      window.removeEventListener("offline", off);
    };
  }, [give]);
  const entry = !isLoaded && give && hasOfflineSession() ? lastSignedInUserId() : null;

  // **연결이 돌아오면 다시 띄운다.** 오프라인으로 들어온 화면에는 Clerk이 없어서
  // 토큰을 못 만든다 — 그 상태로는 밀린 지출을 올려도 401이다(실측).
  //
  // 연결됐는지는 `/health`를 직접 찔러서 본다. 다른 신호는 전부 거짓말을 했다:
  // `online` 이벤트는 안 오고, "요청이 성공했다"는 서비스 워커가 캐시로 답한
  // 것과 구별되지 않아 오프라인인데도 새로고침이 반복됐다(실측).
  useEffect(() => {
    if (!entry) return;
    let stop = false;
    const probe = async () => {
      try {
        const r = await fetch("/health", { cache: "no-store" });
        if (!stop && r.ok) window.location.reload();
      } catch {
        // 아직 끊겨 있다 — 다음 차례에 다시 본다.
      }
    };
    const id = setInterval(probe, 10000);
    window.addEventListener("online", probe);
    return () => {
      stop = true;
      clearInterval(id);
      window.removeEventListener("online", probe);
    };
  }, [entry]);

  return entry;
}

function RequireAuth() {
  const offlineUserId = useOfflineEntry();
  return (
    <>
      <ClerkLoading>
        {offlineUserId ? (
          <SessionGate offlineUserId={offlineUserId}>
            <Outlet />
          </SessionGate>
        ) : (
          <AuthSkeleton />
        )}
      </ClerkLoading>
      <SignedOut>
        <SignOutPurge />
        <Landing />
      </SignedOut>
      <SignedIn>
        <TokenBridge>
          <SessionGate>
            <Outlet />
          </SessionGate>
        </TokenBridge>
      </SignedIn>
    </>
  );
}

/**
 * 로그아웃되면 서비스 워커의 API 캐시를 비운다.
 *
 * `SignedOut` 안에 있으므로 **로그아웃되는 순간 마운트된다.** 첫 방문에도 한 번
 * 도는데 그때는 지울 게 없어 무해하다.
 *
 * 로그인 쪽 짝은 `session-gate.tsx`의 `rememberUser`다(사용자가 바뀌면 비운다).
 * 둘 다 두는 이유: 로그아웃을 못 잡는 경우(탭이 죽는 등)를 로그인 쪽이 받는다.
 */
function SignOutPurge() {
  useEffect(() => {
    clearScreenCache();
    void purgeApiCache();
    try {
      localStorage.removeItem("gotravel:lastUserId");
    } catch {
      // 저장소가 막혀 있어도 캐시는 이미 지웠다.
    }
  }, []);
  return null;
}

function NotFound() {
  return (
    <div className="flex flex-col items-center gap-3 py-24 text-center">
      <p className="text-sm text-muted-foreground">찾을 수 없는 페이지예요.</p>
      <a className="text-sm underline" href="/trips">
        내 여행으로
      </a>
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* 가장 바깥이다 — Clerk 초기화나 라우팅이 터져도 흰 화면 대신 원인이 보인다. */}
    <ErrorBoundary>
      {/*
        Next의 root layout이 하던 두 가지다. **빠져 있으면 조용히 망가진다** —
        Toaster가 없으면 컴포넌트 29곳의 `toast.error(...)`가 아무 데도 안 뜨고
        (오류 피드백 경로가 통째로 사라진다), ThemeProvider가 없으면 테마 토글이
        먹지 않는다. 화면만 보고는 알기 어려워 여기 적어 둔다.
      */}
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
      {/*
        Clerk UI 언어는 브라우저 설정을 따른다(clerk-locale.ts). 안 넣으면 앱
        전체가 한국어인데 로그인 화면만 "Sign in to …"로 뜬다(실측).

        로그인 카드에 보이는 앱 이름은 **여기서 못 바꾼다** — Clerk 대시보드의
        Application name 값이라 거기서 "여행가자"로 고쳐야 한다.
      */}
      {/*
        **`headless`가 핵심이다.** 기본 변형은 로그인된 사용자가 여는 모든
        화면에 Clerk UI 묶음을 딸려 보낸다 — 실측 ui-common 432KB +
        framework 130KB + vendors 133KB = 705KB, 느린 휴대폰에서 다운로드만
        2.9초다(우리 앱 엔트리 전체가 721KB인데 그만큼이 더 붙었다).

        그래서 Clerk UI 컴포넌트를 하나도 쓰지 않는다. 로그인 화면은 직접
        그리고(routes/sign-in.tsx), 가입만 Clerk 호스팅 페이지로 보낸다.
        `SignedIn`·`SignedOut`·`ClerkLoading`은 UI가 아니라 제어용이라 그대로 된다.
      */}
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        clerkJSVariant="headless"
        localization={clerkLocalization()}
        afterSignOutUrl="/"
      >
        <BrowserRouter>
          <Routes>
            {/* 공개 라우트. 참여 버튼만 SignedIn 안에서 보인다. */}
            {/* 공유 링크는 로그인조차 필요 없다 — 토큰이 유일한 문이다. */}
            <Route path="/share/:token" element={<SharePage />} />

            {/* 로그인·회원가입. 랜딩의 "앱 사용하기"가 여기로 보낸다. */}
            <Route path="/sign-in" element={<SignInPage />} />
            <Route path="/sign-up" element={<SignUpPage />} />
            {/* 구글에서 돌아오는 자리. authenticateWithRedirect의 redirectUrl과 같아야 한다. */}
            <Route path="/sso-callback" element={<SsoCallbackPage />} />

            {/*
              도움말은 **로그인 없이** 열린다 — 랜딩에서 "여기가 뭐 하는 곳인가"를
              확인하려는 사람이 첫 대상이다. 껍데기는 로그인 후와 같은 AppLayout을
              쓴다(그쪽은 인증을 요구하지 않는다). 계정 관련 항목만 비어 보인다.
            */}
            <Route element={<AppLayout />}>
              <Route path="/help" element={<HelpPage />} />
            </Route>
            <Route
              path="/join/:inviteCode"
              element={
                <TokenBridge>
                  <JoinPage />
                </TokenBridge>
              }
            />

            <Route element={<RequireAuth />}>
              {/* 관리자는 일반 껍데기(상단 내비·계정 메뉴)를 쓰지 않는다 — Next도
                  /admin이 (app) 레이아웃 바깥이라 자기 머리글만 쓴다. */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="users/:userId" element={<AdminUserDetailPage />} />
                <Route path="trips" element={<AdminTripsPage />} />
                <Route path="trips/:tripId" element={<AdminTripDetailPage />} />
                <Route path="tools" element={<AdminToolsPage />} />
                <Route path="logs" element={<AdminLogsPage />} />
              </Route>

              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/trips" replace />} />
                <Route path="/trips" element={<TripsPage />} />
                <Route path="/trips/new" element={<NewTripPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/trips/:tripId" element={<TripLayout />}>
                  <Route index element={<TripItineraryPage />} />
                  <Route path="checklist" element={<TripChecklistPage />} />
                  <Route path="budget" element={<TripBudgetPage />} />
                  <Route path="map" element={<TripMapPage />} />
                  <Route path="journal" element={<TripJournalPage />} />
                  <Route path="album" element={<TripAlbumPage />} />
                  <Route path="settings" element={<TripSettingsPage />} />
                  <Route path="print" element={<TripPrintPage />} />
                  <Route path="report" element={<TripReportPage />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster />
      </ClerkProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);

// R2에 커스텀 도메인을 붙였다면 그 호스트를 알려준다 — 안 하면 영수증 미리보기가
// 안 뜬다(`isOwnUploadUrl`). `.r2.dev` 개발 URL이면 비워둬도 된다.
// **Worker의 R2_PUBLIC_BASE와 같은 값**이어야 한다. 둘 다 공개 URL이라 비밀이 아니다.
registerUploadHost(import.meta.env.VITE_UPLOAD_BASE);

registerServiceWorker();
